import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConductorAuth } from '../../contexts/ConductorAuthContext';
import conductorApi from '../../lib/conductorApi';
import { API_BASE } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, User, LogOut, KeyRound, Car, ChevronRight, Loader2, MessageSquare, CheckCircle2, XCircle, Repeat, Route } from 'lucide-react';

const estadoStyles = {
  'Agendado':            { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Agendado' },
  'Servicio Recibido':   { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Servicio Recibido' },
  'Realizado':           { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Realizado' },
  'Suspendido':          { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Suspendido' },
};

const formatearFecha = (fecha) => {
  if (!fecha) return '';
  try {
    const [y, m, d] = fecha.split('-');
    const dt = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return dt.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return fecha; }
};

export const ConductorServicios = () => {
  const navigate = useNavigate();
  const { conductor, logout } = useConductorAuth();
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('activos');
  const [empresaLogo, setEmpresaLogo] = useState(null);

  // Acción rápida (modal)
  const [accion, setAccion] = useState(null); // { tipo: 'recepcion'|'realizado'|'suspendido', servicio }
  const [accionTexto, setAccionTexto] = useState('');
  const [accionLoading, setAccionLoading] = useState(false);

  useEffect(() => { loadServicios(); }, []);

  useEffect(() => {
    if (!conductor?.empresa_id) return;
    let active = true;
    let objectUrl = null;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/configuracion/logo?empresa_id=${conductor.empresa_id}&t=${Date.now()}`);
        if (resp.ok && active) {
          const blob = await resp.blob();
          objectUrl = URL.createObjectURL(blob);
          setEmpresaLogo(objectUrl);
        }
      } catch {}
    })();
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [conductor?.empresa_id]);

  const loadServicios = async () => {
    try {
      const response = await conductorApi.get('/conductor/servicios');
      setServicios(response.data || []);
    } catch {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const lista = useMemo(() => {
    if (filtro === 'todos') return servicios;
    if (filtro === 'activos') return servicios.filter(s => !['Realizado', 'Suspendido'].includes(s.estado));
    if (filtro === 'finalizados') return servicios.filter(s => ['Realizado', 'Suspendido'].includes(s.estado));
    return servicios;
  }, [servicios, filtro]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const abrirAccion = (e, tipo, servicio) => {
    e.stopPropagation();
    setAccion({ tipo, servicio });
    setAccionTexto('');
  };

  const cerrarAccion = () => {
    if (accionLoading) return;
    setAccion(null);
    setAccionTexto('');
  };

  const confirmarAccion = async () => {
    if (!accion) return;
    const { tipo, servicio } = accion;
    if (tipo === 'recepcion' && !accionTexto.trim()) {
      toast.error('Debe escribir un mensaje para confirmar la recepción');
      return;
    }
    setAccionLoading(true);
    try {
      if (tipo === 'recepcion') {
        await conductorApi.post(`/conductor/servicios/${servicio.id}/recepcion`, { texto: accionTexto.trim() });
        toast.success('Servicio recibido — el administrador fue notificado');
      } else if (tipo === 'realizado') {
        await conductorApi.post(`/conductor/servicios/${servicio.id}/realizado`, { nota: accionTexto.trim() || null });
        toast.success('Servicio marcado como realizado');
      } else if (tipo === 'suspendido') {
        await conductorApi.post(`/conductor/servicios/${servicio.id}/suspendido`, { nota: accionTexto.trim() || null });
        toast.success('Servicio suspendido');
      }
      setAccion(null);
      setAccionTexto('');
      await loadServicios();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setAccionLoading(false);
    }
  };

  const renderAcciones = (s) => {
    if (s.estado === 'Realizado' || s.estado === 'Suspendido') return null;
    if (s.estado === 'Agendado') {
      return (
        <Button
          type="button"
          onClick={(e) => abrirAccion(e, 'recepcion', s)}
          className="w-full h-10 mt-3 bg-primary"
          data-testid={`btn-recepcion-${s.id}`}
        >
          <MessageSquare size={16} className="mr-2" />
          Marcar Servicio Recibido
        </Button>
      );
    }
    // Servicio Recibido → 2 acciones
    return (
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Button
          type="button"
          variant="outline"
          onClick={(e) => abrirAccion(e, 'suspendido', s)}
          className="h-10 border-red-200 text-red-700 hover:bg-red-50"
          data-testid={`btn-suspender-${s.id}`}
        >
          <XCircle size={16} className="mr-1" /> Suspender
        </Button>
        <Button
          type="button"
          onClick={(e) => abrirAccion(e, 'realizado', s)}
          className="h-10 bg-green-600 hover:bg-green-700"
          data-testid={`btn-realizado-${s.id}`}
        >
          <CheckCircle2 size={16} className="mr-1" /> Realizado
        </Button>
      </div>
    );
  };

  const modalTitle = accion?.tipo === 'recepcion'
    ? 'Marcar Servicio Recibido'
    : accion?.tipo === 'realizado'
      ? 'Confirmar Servicio Realizado'
      : 'Suspender Servicio';

  const modalLabel = accion?.tipo === 'recepcion'
    ? 'Mensaje para el administrador *'
    : accion?.tipo === 'realizado'
      ? 'Nota opcional'
      : 'Motivo de la suspensión (opcional)';

  const modalPlaceholder = accion?.tipo === 'recepcion'
    ? 'Ej: Recibido, voy en camino al punto de recogida.'
    : accion?.tipo === 'realizado'
      ? 'Comentario adicional (opcional)'
      : 'Motivo de la suspensión (opcional)';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pb-8">
      {/* Logo empresa */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-center">
          {empresaLogo ? (
            <img src={empresaLogo} alt="Logo empresa" className="h-10 w-auto object-contain" data-testid="conductor-empresa-logo" />
          ) : (
            <div className="h-10 flex items-center text-gray-400 text-sm">Cargando...</div>
          )}
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-30 shadow-sm">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
              <Car size={22} className="text-primary" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">{conductor?.nombre}</p>
              <p className="text-xs text-gray-500">{conductor?.rut}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/conductor/cambiar-password')} className="p-2 text-gray-500 hover:text-primary rounded-lg hover:bg-gray-50 transition" title="Cambiar contraseña" data-testid="btn-cambiar-password">
              <KeyRound size={18} />
            </button>
            <button onClick={handleLogout} className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-50 transition" title="Cerrar sesión" data-testid="btn-logout">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Saludo */}
      <div className="max-w-2xl mx-auto px-4 pt-5 pb-2">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Manrope']">Hola, {conductor?.nombre?.split(' ')[0] || 'Conductor'} 👋</h1>
        <p className="text-sm text-gray-500 mt-0.5">Aquí están tus servicios asignados.</p>
      </div>

      {/* Tabs */}
      <div className="max-w-2xl mx-auto px-4 pt-3">
        <div className="bg-white rounded-xl p-1 border border-gray-200 flex gap-1 shadow-sm">
          {[
            { key: 'activos',     label: 'Pendientes' },
            { key: 'finalizados', label: 'Finalizados' },
            { key: 'todos',       label: 'Todos' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setFiltro(t.key)}
              className={`flex-1 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${filtro === t.key ? 'bg-primary text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid={`filtro-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">{lista.length} servicio{lista.length !== 1 ? 's' : ''}</p>
      </div>

      {/* List */}
      <main className="max-w-2xl mx-auto px-4 py-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="animate-spin mr-2" size={20} /> Cargando...
          </div>
        ) : lista.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center text-gray-500 border border-gray-100 shadow-sm">
            <Car className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="text-base font-medium text-gray-700">Sin servicios {filtro === 'activos' ? 'pendientes' : filtro === 'finalizados' ? 'finalizados' : ''}</p>
            <p className="text-xs text-gray-400 mt-1">Cuando tengas asignaciones aparecerán aquí.</p>
          </div>
        ) : (
          lista.map(s => {
            const estilo = estadoStyles[s.estado] || estadoStyles['Agendado'];
            return (
              <div
                key={s.id}
                onClick={() => navigate(`/conductor/servicio/${s.id}`)}
                className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md hover:border-primary/40 transition cursor-pointer"
                data-testid={`servicio-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                    <Calendar size={14} className="text-primary" />
                    <span className="capitalize">{formatearFecha(s.fecha)}</span>
                    <Clock size={14} className="text-primary ml-1" />
                    <span className="font-medium text-gray-900">{s.hora}</span>
                    {s.tiene_retorno && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
                        <Repeat size={10} /> Ida y vuelta
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${estilo.bg} ${estilo.text} shrink-0`}>{estilo.label}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 font-medium mb-2">
                  <User size={14} className="text-gray-400" />
                  <span className="truncate">{s.nombre_pasajero || s.pasajeros || 'Sin pasajero'}</span>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  <div className="flex items-start gap-2">
                    <MapPin size={12} className="text-green-600 mt-0.5 shrink-0" />
                    <span className="truncate">{s.origen}</span>
                  </div>
                  {(s.desvios || []).length > 0 && (
                    <div className="flex items-start gap-2 text-amber-700">
                      <Route size={12} className="mt-0.5 shrink-0" />
                      <span className="truncate">{(s.desvios || []).length} desvío{(s.desvios || []).length !== 1 ? 's' : ''} intermedio{(s.desvios || []).length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <MapPin size={12} className="text-red-600 mt-0.5 shrink-0" />
                    <span className="truncate">{s.destino}</span>
                  </div>
                </div>

                {/* Acciones rápidas */}
                {renderAcciones(s)}

                <div
                  className="flex items-center justify-end mt-2 text-primary text-xs font-medium"
                  onClick={(e) => { e.stopPropagation(); navigate(`/conductor/servicio/${s.id}`); }}
                >
                  Ver detalle <ChevronRight size={14} className="ml-1" />
                </div>
              </div>
            );
          })
        )}
      </main>

      {/* Modal de acción */}
      <Dialog open={!!accion} onOpenChange={(v) => { if (!v) cerrarAccion(); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:max-w-md p-4 sm:p-6 overflow-hidden" data-testid="modal-accion-rapida">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg break-words">{modalTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 min-w-0">
            {accion?.servicio && (
              <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-600 border border-gray-200 min-w-0 break-words">
                <p className="font-medium text-gray-900 break-words">{accion.servicio.nombre_pasajero || accion.servicio.pasajeros}</p>
                <p className="break-words">{formatearFecha(accion.servicio.fecha)} • {accion.servicio.hora}</p>
                <p className="mt-1 break-words leading-snug">
                  <span className="font-medium text-green-700">{accion.servicio.origen}</span>
                  <span className="text-gray-400"> → </span>
                  <span className="font-medium text-red-700">{accion.servicio.destino}</span>
                </p>
              </div>
            )}
            <Label className="text-sm">{modalLabel}</Label>
            <Textarea
              value={accionTexto}
              onChange={(e) => setAccionTexto(e.target.value)}
              placeholder={modalPlaceholder}
              rows={3}
              className="w-full resize-none"
              data-testid="accion-texto-input"
            />
            {accion?.tipo === 'recepcion' && (
              <p className="text-xs text-gray-500 leading-snug">El mensaje queda registrado con la hora actual y será visible para el administrador.</p>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={cerrarAccion} className="w-full sm:w-auto" disabled={accionLoading}>Cancelar</Button>
            <Button
              onClick={confirmarAccion}
              disabled={accionLoading || (accion?.tipo === 'recepcion' && !accionTexto.trim())}
              className={`w-full sm:w-auto ${accion?.tipo === 'realizado' ? 'bg-green-600 hover:bg-green-700' : accion?.tipo === 'suspendido' ? 'bg-red-600 hover:bg-red-700' : ''}`}
              data-testid="accion-submit"
            >
              {accionLoading ? 'Guardando...' : accion?.tipo === 'recepcion' ? 'Confirmar' : accion?.tipo === 'realizado' ? 'Realizado' : 'Suspender'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
